// LOAD-BEARING SIDE-EFFECT IMPORT — do not delete as "unused". This delegate
// used to import from BOTH hub shims (`scribeAPIV2` for the V2 endpoint and
// `scribeAPIV2_1` for its sibling); the V2 specifier is the one that carried the
// Rematch model warm-up, so it is the one kept. See the
// `useQueryEhrLaunchPastNotes.ts` delegate for the measurement.
import '@/pages/api/scribeAPIV2';

import { useAmdMarkdownSectionalEditorEnabled } from '@/hooks/integrations/useAmdMarkdownSectionalEditorEnabled';
import { useIntegrationEhrInfo } from '@/hooks/integrations/useIntegrationEhrInfo';
import { useIsFhirAuth } from '@/hooks/integrations/useIsFhirAuth';
import { IntegrationLaunchSource } from '@heidi/integrations/ehr/contracts';
import { useQueryAvailableStandardizedSections as useQueryAvailableStandardizedSectionsCore } from '@heidi/integrations/ehr/data';
import { type EHRProvider } from '@heidi/schema/ml-scribe';

/**
 * Delegate. THREE inputs resolve here and nothing else does.
 *
 * `useIntegrationEhrInfo` composes four app resolvers over the Rematch store, a
 * flag and two app queries; `useIsFhirAuth` is a raw `useSelector` on
 * `state.auth.authProvider`; `useAmdMarkdownSectionalEditorEnabled` is a
 * Flagsmith read, and flags stay app-resolved by rule. All three are INPUTS to
 * the request, so the package takes their values and keeps the cache identity,
 * the request and the V2/V2_1 selection.
 *
 * The generic-launch asymmetry is composed HERE, in one expression, because both
 * of its terms are app facts: generic/medtech launch takes its onboarding
 * session id from the launch context, so the legacy FHIR suppression does not
 * apply to it.
 */
export const useQueryAvailableStandardizedSections = (
  ehrProviderFromSource?: EHRProvider | null
) => {
  const {
    integrationOnboardingSessionId,
    canSyncSectionalNotes,
    integrationLaunchSource,
    integrationEhrProvider,
  } = useIntegrationEhrInfo(ehrProviderFromSource);

  const isAmdMarkdownSectionsCatalogue =
    useAmdMarkdownSectionalEditorEnabled(integrationEhrProvider);

  const isFhirAuth = useIsFhirAuth();
  const isGenericLaunch =
    integrationLaunchSource === IntegrationLaunchSource.GENERIC_EHR_LAUNCH;

  return useQueryAvailableStandardizedSectionsCore({
    integrationOnboardingSessionId,
    isAmdMarkdownSectionsCatalogue,
    enabled: (isGenericLaunch || !isFhirAuth) && canSyncSectionalNotes,
  });
};
